// ============================================================================
// fordJavaslatView.js — a FORDÍTÁSJAVASLAT megjelenítése a hozzászólás alatt (2026-10-19, v107). Lásd fordJavaslat.js és a szerver
// functions/src/translationSuggestion.js. Minden szöveg a felület nyelvén (nyelvek.json, message.fix*).
//
//   - "✎ Fordítás javítása": szerkesztő a mostani fordítással előtöltve + kötelező indoklás; a javaslat önmagában nem változtat;
//   - "✎ N nyitott javaslat": lista (javasolta, idő, indoklás, javasolt szöveg a mostanival összevetve); aki dönthet: elfogadás / elutasítás;
//   - "✎ Elfogadott javítás": ki javasolta, ki fogadta el, mikor (önjóváhagyás és AI-jelzés jelölve);
//   - "📜 Fordítás-előzmény": a kicserélt (régi) fordítások, összecsukva.
// ============================================================================

import { t, localeFor } from "./i18n.js";
import { el } from "./dom.js";
import { emberiJavitas, nyitottSzam, javaslatElavult, elozmenyek, eldonthet, visszavonhato } from "./fordJavaslat.js";


function datum(ms, uiLang) {
  if (typeof ms !== "number" || ms === 0) return "";
  try {
    return new Date(ms).toLocaleString(localeFor(uiLang), { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return new Date(ms).toISOString().slice(0, 16);
  }
}

const nevVagyTorolt = (nev, uid, uiLang) => (uid === "deleted" ? t(uiLang, "msg.deletedSender") : nev || "?");

/**
 * @param {object} msg a hozzászólás
 * @param {string|null} nyelv a MEGJELENÍTETT fordítás nyelve (null: nincs fordítás, pl. az eredetit nézi)
 * @param {object} es { fixEditor, fixList, isStaff, tagKezelo }
 * @param {object} handlers onFixOpen, onFixChange, onFixCancel, onFixSend, onFixToggle, onFixDecide, onFixWithdraw
 * @returns {HTMLElement|null}
 */
export function renderFordJavaslat(msg, nyelv, uiLang, myUid, es, handlers) {
  if (!msg || !msg.messageId || msg.deleted === true || !nyelv || nyelv === msg.sourceLang || !handlers || !handlers.onFixOpen) return null;
  const mostani = msg.translations && typeof msg.translations === "object" ? msg.translations[nyelv] : undefined;
  if (typeof mostani !== "string" || mostani.trim() === "") return null;
  const szerkeszto = es && es.fixEditor && es.fixEditor.messageId === msg.messageId && es.fixEditor.lang === nyelv ? es.fixEditor : null;
  const lista = es && es.fixList && es.fixList.messageId === msg.messageId && es.fixList.lang === nyelv ? es.fixList : null;
  const nyitott = nyitottSzam(msg, nyelv);
  const javitas = emberiJavitas(msg, nyelv);
  const elozm = elozmenyek(msg, nyelv);
  const doboz = el("div", { class: "msg-fix" });

  // --- Sor: a javítás gombja és a nyitott javaslatok jelzése
  const sor = el("div", { class: "msg-fix-row" });
  if (!szerkeszto) {
    sor.appendChild(el("button", { type: "button", class: "text-btn msg-fix-btn", text: "✎ " + t(uiLang, "message.fixTranslation"), onclick: () => handlers.onFixOpen(msg.messageId, nyelv, mostani) }));
  }
  if (nyitott > 0 || lista) {
    sor.appendChild(el("button", {
      type: "button", class: "text-btn msg-fix-count", text: "✎ " + t(uiLang, "message.fixPending", { n: nyitott }),
      "aria-expanded": lista ? "true" : "false", onclick: () => handlers.onFixToggle(msg.messageId, nyelv),
    }));
  }
  doboz.appendChild(sor);

  // --- Az elfogadott javítás jelzése
  if (javitas) {
    const szoveg = t(uiLang, "message.fixApplied", {
      by: nevVagyTorolt(javitas.byName, javitas.by, uiLang), decider: nevVagyTorolt(javitas.decidedByName, javitas.decidedBy, uiLang), time: datum(javitas.at, uiLang),
    }) + (javitas.selfApproved ? " " + t(uiLang, "message.fixSelf") : "") + (javitas.aiNote ? ` · ${javitas.aiNote}` : "");
    doboz.appendChild(el("div", { class: "msg-fix-applied", title: javitas.note || null, text: "✎ " + szoveg }));
  }

  // --- A szerkesztő
  if (szerkeszto) {
    const szerk = el("div", { class: "msg-fix-editor" });
    szerk.appendChild(el("p", { class: "hint", text: t(uiLang, "message.fixHint") }));
    szerk.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "message.fixProposedLabel") }));
    const szovegMezo = el("textarea", { class: "panel-input", rows: "4", maxlength: "8000" });
    szovegMezo.value = szerkeszto.text || "";
    szovegMezo.addEventListener("input", () => handlers.onFixChange("text", szovegMezo.value));
    szerk.appendChild(szovegMezo);
    szerk.appendChild(el("label", { class: "panel-label small", text: t(uiLang, "message.fixReasonLabel") }));
    const indokMezo = el("input", { class: "panel-input", type: "text", maxlength: "300", placeholder: t(uiLang, "message.fixReasonExample") });
    indokMezo.value = szerkeszto.reason || "";
    indokMezo.addEventListener("input", () => handlers.onFixChange("reason", indokMezo.value));
    szerk.appendChild(indokMezo);
    if (szerkeszto.error) szerk.appendChild(el("div", { class: "panel-status err", role: "alert", text: szerkeszto.error }));
    const gombok = el("div", { class: "panel-row" });
    gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "glossary.cancel"), disabled: szerkeszto.busy === true, onclick: handlers.onFixCancel }));
    gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: szerkeszto.busy ? "…" : t(uiLang, "message.fixSend"), disabled: szerkeszto.busy === true, onclick: handlers.onFixSend }));
    szerk.appendChild(gombok);
    doboz.appendChild(szerk);
  }

  // --- A javaslatok listája
  if (lista) {
    const dobozLista = el("div", { class: "msg-fix-list" });
    if (lista.hiba) dobozLista.appendChild(el("div", { class: "panel-status err", role: "alert", text: lista.hiba }));
    if (lista.betolt) dobozLista.appendChild(el("p", { class: "hint", text: t(uiLang, "common.loading") }));
    const nyitottak = (lista.lista || []).filter((j) => j.status === "open");
    if (!lista.betolt && nyitottak.length === 0) dobozLista.appendChild(el("p", { class: "hint", text: t(uiLang, "message.fixNone") }));
    for (const j of nyitottak) {
      const elavult = javaslatElavult(j, msg, nyelv);
      const kartya = el("div", { class: "msg-fix-card" + (elavult ? " stale" : "") });
      kartya.appendChild(el("div", { class: "msg-fix-head", text: t(uiLang, "message.fixBy", { name: nevVagyTorolt(j.byName, j.by, uiLang), time: datum(j.at, uiLang) }) + (j.aiNote ? ` · ${j.aiNote}` : "") }));
      kartya.appendChild(el("div", { class: "msg-fix-reason", text: j.reason }));
      kartya.appendChild(el("div", { class: "msg-fix-label", text: t(uiLang, "message.fixCurrentLabel") }));
      kartya.appendChild(el("div", { class: "msg-fix-text old", lang: nyelv, text: mostani }));
      kartya.appendChild(el("div", { class: "msg-fix-label", text: t(uiLang, "message.fixProposedLabel") }));
      kartya.appendChild(el("div", { class: "msg-fix-text new", lang: nyelv, text: j.proposedText }));
      if (elavult) kartya.appendChild(el("div", { class: "panel-status err", text: t(uiLang, "message.fixStale") }));
      const lehet = eldonthet(j, msg, myUid, es);
      const visszavon = visszavonhato(j, myUid, es);
      if (lehet) {
        const megj = el("input", { class: "panel-input", type: "text", maxlength: "300", placeholder: t(uiLang, "message.fixNoteLabel"), "aria-label": t(uiLang, "message.fixNoteLabel") });
        megj.addEventListener("input", () => handlers.onFixNote(j.id, megj.value));
        kartya.appendChild(megj);
      }
      const gombok = el("div", { class: "panel-row" });
      if (lehet) {
        gombok.appendChild(el("button", { type: "button", class: "primary-btn compact", text: t(uiLang, "message.fixAccept"), disabled: lista.busy === true, onclick: () => handlers.onFixDecide(msg.messageId, nyelv, j.id, "accept") }));
        gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "message.fixReject"), disabled: lista.busy === true, onclick: () => handlers.onFixDecide(msg.messageId, nyelv, j.id, "reject") }));
      }
      if (visszavon) {
        gombok.appendChild(el("button", { type: "button", class: "neutral-btn", text: t(uiLang, "message.fixWithdraw"), disabled: lista.busy === true, onclick: () => handlers.onFixWithdraw(msg.messageId, nyelv, j.id) }));
      }
      if (gombok.childNodes.length > 0) kartya.appendChild(gombok);
      dobozLista.appendChild(kartya);
    }
    doboz.appendChild(dobozLista);
  }

  // --- Fordítás-előzmény
  if (elozm.length > 0) {
    const reszletek = el("details", { class: "msg-fix-history" });
    reszletek.appendChild(el("summary", { text: "📜 " + t(uiLang, "message.fixHistory", { n: elozm.length }) }));
    for (const e of elozm) {
      const cimke = e.kind === "machine-overwrote"
        ? t(uiLang, "message.fixHistoryMachine", { time: datum(e.at, uiLang) })
        : t(uiLang, "message.fixHistoryReplaced", { name: nevVagyTorolt(e.byName, e.by, uiLang), time: datum(e.at, uiLang) });
      reszletek.appendChild(el("div", { class: "msg-fix-history-row" }, [el("div", { class: "msg-review-log-head", text: cimke }), el("div", { class: "msg-review-log-text", lang: nyelv, text: e.text })]));
    }
    doboz.appendChild(reszletek);
  }

  return doboz;
}
